import "@ui5/webcomponents-icons/dist/sys-help.js";
import "@ui5/webcomponents-icons/dist/accept.js";
import "@ui5/webcomponents-icons/dist/building.js";
import "@ui5/webcomponents-icons/dist/globe.js";
import "@ui5/webcomponents-icons/dist/keyboard-and-mouse.js";
import "@ui5/webcomponents-icons/dist/locked.js";
import "@ui5/webcomponents-icons/dist/menu2.js";
import "@ui5/webcomponents-icons/dist/palette.js";
import {
  Avatar,
  Menu,
  MenuItem,
  NavigationLayout,
  ShellBar,
  ShellBarItem,
  type InputDomRef,
  type MenuDomRef,
  type ShellBarItemPropTypes,
} from "@ui5/webcomponents-react";
import { isLanguage, supportedLanguages, type Language } from "@positive/i18n";
import { useCallback, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import lockupDark from "../brand/positive-lockup-dark.svg?url";
import lockupLight from "../brand/positive-lockup-light.svg?url";
import { NotificationCenter, type ShellNotification } from "../notifications/NotificationCenter";
import { IdleLockDialog, type IdleLockDialogProps } from "../session/IdleLockDialog";
import { idleTimeoutDefaults, useIdleLock } from "../session/useIdleLock";
import { useShortcut, useShortcutHelp } from "../shortcuts/ShortcutProvider";
import { isThemeKey, themeKeys, type ThemeKey } from "../theme/theme";
import type { NavigationEntry } from "./navigation";
import { SideNav } from "./SideNav";
import "./AppShell.css";

export interface AppShellProps {
  navigation: readonly NavigationEntry[];
  currentPath: string;
  language: Language;
  theme: ThemeKey;
  onNavigate: (path: string) => void;
  onLanguageChange: (language: Language) => void;
  onThemeChange: (theme: ThemeKey) => void;
  /** Notification centre entries (D14-9), newest first. */
  notifications?: readonly ShellNotification[];
  /** Unread count from the server (the list may be one page only); defaults to the unread entries of the list. */
  notificationsUnread?: number;
  onNotificationsMarkAllRead?: () => void;
  onNotificationOpen?: (notification: ShellNotification) => void;
  /** Idle lock timeout in ms (P4); defaults to the back-office value, `0` disables. */
  idleTimeoutMs?: number;
  /** How the idle lock is lifted: back-office re-authentication or POS PIN (P4). */
  idleLock?: Omit<IdleLockDialogProps, "open" | "onUnlock">;
  /** Signed-in user shown as the Shell Bar avatar, with the user menu. */
  user?: { name: string; initials: string };
  /** User menu entries (already translated), e.g. My profile, Change password, Sign out. */
  userMenuItems?: readonly UserMenuItem[];
  onUserMenuItem?: (key: string) => void;
  /** Current company / branch (P12) with the switcher. */
  context?: { label: string; onClick: () => void };
  /** Banner above the page content (e.g. operator support session, A3). */
  banner?: ReactNode;
  /** Context help (D16): opens the user manual's page of the current screen. */
  onHelp?: () => void;
  children: ReactNode;
}

export interface UserMenuItem {
  key: string;
  text: string;
  icon: string;
}

type OpenMenu = "language" | "theme" | "user" | null;
const noUserMenu: readonly UserMenuItem[] = [];
const noNotifications: readonly ShellNotification[] = [];

/**
 * Fiori shell (ADR-0010/D-2): Shell Bar with language and theme switches, notifications and user menu
 * placeholders, and module side navigation generated from the registry. All texts come from i18next.
 * Logo (decision BR7): the Positive horizontal lockup in SAP blue at the start of the Shell Bar (top-left in LTR,
 * top-right in RTL; the artwork itself is never mirrored), named "Positive ERP" for assistive technology, followed by
 * the company/branch context. No separate product label.
 */
export function AppShell({
  navigation,
  currentPath,
  language,
  theme,
  onNavigate,
  onLanguageChange,
  onThemeChange,
  notifications = noNotifications,
  notificationsUnread,
  onNotificationsMarkAllRead,
  onNotificationOpen,
  idleTimeoutMs = idleTimeoutDefaults.backOffice,
  idleLock,
  user,
  userMenuItems = noUserMenu,
  onUserMenuItem,
  context,
  banner,
  onHelp,
  children,
}: AppShellProps) {
  const { t, i18n } = useTranslation("shell");
  const [collapsed, setCollapsed] = useState(false);
  const menuSearchRef = useRef<InputDomRef>(null);
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
  const [opener, setOpener] = useState<HTMLElement | null>(null);
  const menuRef = useRef<MenuDomRef>(null);
  const [notificationsOpener, setNotificationsOpener] = useState<HTMLElement | null>(null);
  const shortcutHelp = useShortcutHelp();
  const idle = useIdleLock({ timeoutMs: idleTimeoutMs });
  const unread = notificationsUnread ?? notifications.filter((item) => !item.read).length;

  // Menu search matches the labels in every language, so "invoice" also finds "الفواتير" in the Arabic UI.
  const labelsOf = useCallback(
    (entry: NavigationEntry) => supportedLanguages.map((code) => i18n.getFixedT(code, "shell")(entry.labelKey)),
    [i18n],
  );
  const focusMenuSearch = useCallback(() => {
    setCollapsed(false);
    requestAnimationFrame(() => {
      void menuSearchRef.current?.focus();
    });
  }, []);
  useShortcut("menuSearch", focusMenuSearch);

  const openFrom =
    (menu: Exclude<OpenMenu, null>): NonNullable<ShellBarItemPropTypes["onClick"]> =>
    (event) => {
      setOpener(event.detail.targetRef);
      setOpenMenu(menu);
    };

  const navigateFromMenu = useCallback(
    (path: string) => {
      // On a phone the navigation covers the page: close it once a screen is chosen (as UI5's Navigation Layout does).
      if (window.innerWidth < 600) {
        setCollapsed(true);
      }
      onNavigate(path);
    },
    [onNavigate],
  );

  return (
    <NavigationLayout
      className="positive-shell-layout"
      mode={collapsed ? "Collapsed" : "Expanded"}
      header={
        <ShellBar
          logo={
            <img
              className="positive-shell-logo"
              src={theme === "evening" ? lockupDark : lockupLight}
              alt={t("productTitle")}
              data-testid="shell-logo"
            />
          }
          accessibilityAttributes={{ logo: { name: t("productTitle") } }}
          onLogoClick={() => {
            onNavigate("/");
          }}
          {...(context
            ? {
                secondaryTitle: context.label,
                // Without a primary title UI5 hides the secondary title, so the context is shown in the content area.
                content: (
                  <span className="positive-shell-context" data-testid="shell-context">
                    {context.label}
                  </span>
                ),
              }
            : {})}
          showNotifications
          notificationsCount={unread > 0 ? String(unread) : undefined}
          onNotificationsClick={(event) => {
            event.preventDefault();
            setNotificationsOpener(event.detail.targetRef);
          }}
          data-testid="shell-bar"
          {...(user
            ? {
                profile: <Avatar initials={user.initials} accessibleName={user.name} data-testid="user-avatar" />,
                onProfileClick: (event) => {
                  setOpener(event.detail.targetRef);
                  setOpenMenu("user");
                },
              }
            : {})}
          startButton={
            <ShellBarItem
              icon="menu2"
              text={t("toggleNavigation")}
              data-testid="toggle-navigation"
              stable-dom-ref="toggle-navigation"
              onClick={() => {
                setCollapsed((value) => !value);
              }}
            />
          }
        >
          {context && (
            <ShellBarItem
              icon="building"
              text={t("context.switch", { context: context.label })}
              data-testid="context-switch"
              stable-dom-ref="context-switch"
              onClick={context.onClick}
            />
          )}
          <ShellBarItem
            icon="globe"
            text={t("language.label")}
            data-testid="language-switch"
            stable-dom-ref="language-switch"
            onClick={openFrom("language")}
          />
          <ShellBarItem
            icon="palette"
            text={t("theme.label")}
            data-testid="theme-switch"
            stable-dom-ref="theme-switch"
            onClick={openFrom("theme")}
          />
          {onHelp && (
            <ShellBarItem
              icon="sys-help"
              text={t("help")}
              data-testid="help-button"
              stable-dom-ref="help-button"
              onClick={onHelp}
            />
          )}
          <ShellBarItem
            icon="keyboard-and-mouse"
            text={t("shortcuts")}
            data-testid="shortcut-help-button"
            stable-dom-ref="shortcut-help-button"
            onClick={shortcutHelp.open}
          />
          <ShellBarItem
            icon="locked"
            text={t("lockScreen")}
            data-testid="lock-screen"
            stable-dom-ref="lock-screen"
            onClick={idle.lock}
          />
        </ShellBar>
      }
      sideContent={
        <SideNav
          navigation={navigation}
          currentPath={currentPath}
          collapsed={collapsed}
          onNavigate={navigateFromMenu}
          labelsOf={labelsOf}
          searchRef={menuSearchRef}
          onExpandRequest={focusMenuSearch}
        />
      }
    >
      {/* Locked: content stays mounted (no data loss) but is not visible behind the lock dialog. */}
      <main className={idle.locked ? "positive-shell-content positive-shell-locked" : "positive-shell-content"}>
        {banner}
        {children}
      </main>
      <IdleLockDialog open={idle.locked} onUnlock={idle.unlock} {...idleLock} />
      <NotificationCenter
        open={notificationsOpener !== null}
        opener={notificationsOpener}
        notifications={notifications}
        onClose={() => {
          setNotificationsOpener(null);
        }}
        {...(onNotificationsMarkAllRead ? { onMarkAllRead: onNotificationsMarkAllRead } : {})}
        onOpen={(notification) => {
          setNotificationsOpener(null);
          onNotificationOpen?.(notification);
        }}
      />
      <Menu
        ref={menuRef}
        open={openMenu !== null}
        opener={opener}
        headerText={
          openMenu === "theme" ? t("theme.label") : openMenu === "user" ? (user?.name ?? "") : t("language.label")
        }
        onClose={() => {
          setOpenMenu(null);
        }}
        onItemClick={(event) => {
          const value = event.detail.item.dataset["value"];
          if (openMenu === "language" && isLanguage(value)) {
            onLanguageChange(value);
          } else if (openMenu === "theme" && isThemeKey(value)) {
            onThemeChange(value);
          } else if (openMenu === "user" && value) {
            onUserMenuItem?.(value);
          }
        }}
      >
        {openMenu === "language" &&
          supportedLanguages.map((code) => (
            <MenuItem
              key={code}
              data-value={code}
              text={t(`language.${code}`)}
              icon={code === language ? "accept" : undefined}
            />
          ))}
        {openMenu === "theme" &&
          themeKeys.map((key) => (
            <MenuItem key={key} data-value={key} text={t(`theme.${key}`)} icon={key === theme ? "accept" : undefined} />
          ))}
        {openMenu === "user" &&
          userMenuItems.map((item) => (
            <MenuItem key={item.key} data-value={item.key} text={item.text} icon={item.icon} />
          ))}
      </Menu>
    </NavigationLayout>
  );
}
